<script setup>
import { ref, computed, onMounted } from 'vue';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import Spinner from 'shared/components/Spinner.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import PrestaShopAPI from 'dashboard/api/integrations/prestashop';

const store = useStore();
const { t } = useI18n();

const integrationLoaded = ref(false);
const errorMessage = ref('');
const successMessage = ref('');

// Add store form
const showAddForm = ref(false);
const formStoreName = ref('');
const formUrl = ref('');
const formApiKey = ref('');
const formAdminPath = ref('');
const formInboxIds = ref([]);
const testingConnection = ref(false);
const testResult = ref(null);
const savingStore = ref(false);

// Edit store
const editingHookId = ref(null);
const editStoreName = ref('');
const editUrl = ref('');
const editApiKey = ref('');
const editAdminPath = ref('');
const editInboxIds = ref([]);
const editTestResult = ref(null);
const editTestingConnection = ref(false);
const savingEdit = ref(false);

// Delete dialog
const dialogRef = ref(null);
const hookToDelete = ref(null);

// Stores list
const stores = ref([]);
const loadingStores = ref(false);

// Inboxes
const inboxes = ref([]);

// Search
const searchQuery = ref('');
const searchType = ref('email');
const searchHookId = ref(null);
const searching = ref(false);
const searchResults = ref(null);
const searchCustomer = ref(null);

// Order details
const selectedOrder = ref(null);
const loadingOrderDetails = ref(false);
const orderDetails = ref(null);

const integration = computed(() => {
  return store.getters['integrations/getIntegration']('prestashop');
});

const isConnected = computed(() => {
  return stores.value.length > 0;
});

async function fetchStores() {
  loadingStores.value = true;
  try {
    const { data } = await PrestaShopAPI.getStores();
    stores.value = data.stores || [];
  } catch (e) {
    console.error('[PrestaShop] Error fetching stores:', e);
  } finally {
    loadingStores.value = false;
  }
}

async function fetchInboxes() {
  try {
    await store.dispatch('inboxes/get');
    const allInboxes = store.getters['inboxes/getInboxes'];
    inboxes.value = allInboxes || [];
  } catch (e) {
    console.error('[PrestaShop] Error fetching inboxes:', e);
  }
}

async function testConnection(url, apiKey) {
  try {
    const { data } = await PrestaShopAPI.testConnection(url, apiKey);
    return data;
  } catch (e) {
    return { success: false, message: e.response?.data?.message || e.message };
  }
}

async function handleTestConnection() {
  testingConnection.value = true;
  testResult.value = null;
  const result = await testConnection(formUrl.value, formApiKey.value);
  testResult.value = result;
  testingConnection.value = false;
}

async function handleEditTestConnection() {
  editTestingConnection.value = true;
  editTestResult.value = null;
  const result = await testConnection(editUrl.value, editApiKey.value);
  editTestResult.value = result;
  editTestingConnection.value = false;
}

async function addStore() {
  if (!formStoreName.value || !formUrl.value || !formApiKey.value) {
    errorMessage.value = 'Todos los campos son obligatorios';
    return;
  }
  savingStore.value = true;
  errorMessage.value = '';
  try {
    await store.dispatch('integrations/createHook', {
      app_id: 'prestashop',
      settings: {
        store_name: formStoreName.value,
        url: formUrl.value.replace(/\/+$/, ''),
        api_key: formApiKey.value,
        admin_path: formAdminPath.value.replace(/^\/|\/$/g, ''),
        inbox_ids: formInboxIds.value,
      },
    });
    useAlert('Tienda conectada correctamente');
    formStoreName.value = '';
    formUrl.value = '';
    formApiKey.value = '';
    formAdminPath.value = '';
    formInboxIds.value = [];
    testResult.value = null;
    showAddForm.value = false;
    await fetchStores();
  } catch (e) {
    errorMessage.value = e?.message || 'Error al conectar la tienda';
  } finally {
    savingStore.value = false;
  }
}

function startEdit(storeItem) {
  editingHookId.value = storeItem.id;
  editStoreName.value = storeItem.store_name;
  editUrl.value = storeItem.url;
  editApiKey.value = '';
  editAdminPath.value = storeItem.admin_path || '';
  editInboxIds.value = storeItem.inbox_ids || [];
  editTestResult.value = null;
}

function cancelEdit() {
  editingHookId.value = null;
  editTestResult.value = null;
}

async function saveEdit() {
  if (!editStoreName.value || !editUrl.value) {
    errorMessage.value = 'Nombre y URL son obligatorios';
    return;
  }
  savingEdit.value = true;
  errorMessage.value = '';
  try {
    const settings = {
      store_name: editStoreName.value,
      url: editUrl.value.replace(/\/+$/, ''),
      admin_path: editAdminPath.value.replace(/^\/|\/$/g, ''),
      inbox_ids: editInboxIds.value,
    };
    if (editApiKey.value) {
      settings.api_key = editApiKey.value;
    }
    await PrestaShopAPI.updateHook(editingHookId.value, settings);
    useAlert('Tienda actualizada');
    editingHookId.value = null;
    await fetchStores();
  } catch (e) {
    errorMessage.value = e?.response?.data?.error || e?.message || 'Error al actualizar';
  } finally {
    savingEdit.value = false;
  }
}

function openDeleteDialog(storeItem) {
  hookToDelete.value = storeItem;
  dialogRef.value?.open();
}

async function confirmDelete() {
  if (!hookToDelete.value) return;
  try {
    await store.dispatch('integrations/deleteHook', {
      appId: 'prestashop',
      hookId: hookToDelete.value.id,
    });
    dialogRef.value?.close();
    useAlert('Tienda eliminada');
    hookToDelete.value = null;
    await fetchStores();
  } catch (e) {
    errorMessage.value = e?.message || 'Error al eliminar';
  }
}

async function searchOrders() {
  if (!searchQuery.value || !searchHookId.value) {
    errorMessage.value = 'Selecciona una tienda e introduce un criterio de busqueda';
    return;
  }
  searching.value = true;
  searchResults.value = null;
  searchCustomer.value = null;
  orderDetails.value = null;
  selectedOrder.value = null;
  errorMessage.value = '';
  try {
    const { data } = await PrestaShopAPI.searchOrders(
      searchHookId.value,
      searchQuery.value,
      searchType.value
    );
    searchResults.value = data.orders || [];
    searchCustomer.value = data.customer || null;
    if (data.message && (!data.orders || data.orders.length === 0)) {
      successMessage.value = data.message;
    }
  } catch (e) {
    errorMessage.value = e.response?.data?.error || e.message;
  } finally {
    searching.value = false;
  }
}

async function viewOrderDetails(order) {
  selectedOrder.value = order;
  loadingOrderDetails.value = true;
  orderDetails.value = null;
  try {
    const hookId = order.hook_id || searchHookId.value;
    const { data } = await PrestaShopAPI.getOrderDetails(hookId, order.id);
    orderDetails.value = data.order;
  } catch (e) {
    errorMessage.value = e.response?.data?.error || 'Error al cargar detalles';
  } finally {
    loadingOrderDetails.value = false;
  }
}

function closeOrderDetails() {
  selectedOrder.value = null;
  orderDetails.value = null;
}

function formatCurrency(amount, currency) {
  if (!amount) return '-';
  return new Intl.NumberFormat('es-ES', {
    style: 'currency',
    currency: currency || 'EUR',
  }).format(amount);
}

function formatDate(dateString) {
  if (!dateString) return '-';
  return new Date(dateString).toLocaleDateString('es-ES', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function getInboxName(inboxId) {
  const inbox = inboxes.value.find(i => i.id === Number(inboxId));
  return inbox ? inbox.name : `Inbox #${inboxId}`;
}

function toggleInboxId(storeInboxIds, inboxId) {
  const id = String(inboxId);
  const idx = storeInboxIds.indexOf(id);
  if (idx > -1) {
    storeInboxIds.splice(idx, 1);
  } else {
    storeInboxIds.push(id);
  }
}

onMounted(async () => {
  await store.dispatch('integrations/get');
  await fetchInboxes();
  await fetchStores();
  integrationLoaded.value = true;
});
</script>

<template>
  <div
    v-if="integrationLoaded"
    class="flex flex-col flex-1 overflow-auto gap-5 pt-1 pb-10"
  >
    <!-- Integration header card -->
    <div
      class="flex flex-col items-start justify-between lg:flex-row lg:items-center p-6 outline outline-n-container outline-1 bg-n-alpha-3 rounded-md shadow gap-6"
    >
      <div
        class="flex items-start lg:items-center justify-start flex-1 m-0 gap-6 flex-col lg:flex-row"
      >
        <div class="flex h-16 w-16 items-center justify-center flex-shrink-0">
          <img
            :src="`/dashboard/images/integrations/${integration.id || 'prestashop'}.png`"
            class="max-w-full rounded-md border border-n-weak shadow-sm block dark:hidden bg-n-alpha-3 dark:bg-n-alpha-2"
          />
          <img
            :src="`/dashboard/images/integrations/${integration.id || 'prestashop'}-dark.png`"
            class="max-w-full rounded-md border border-n-weak shadow-sm hidden dark:block bg-n-alpha-3 dark:bg-n-alpha-2"
          />
        </div>
        <div>
          <h3 class="mb-1 text-xl font-medium text-n-slate-12">
            {{ integration.name || 'PrestaShop' }}
          </h3>
          <p class="text-n-slate-11 text-sm leading-6">
            {{ integration.description || 'Conecta tus tiendas PrestaShop para ver pedidos de clientes directamente en Chatwoot.' }}
          </p>
        </div>
      </div>
      <div class="flex justify-center items-center mb-0">
        <Button
          faded
          blue
          label="Añadir tienda"
          @click="showAddForm = !showAddForm"
        />
      </div>
    </div>

    <!-- Messages -->
    <div
      v-if="errorMessage"
      class="p-3 rounded-md bg-n-ruby-2 text-n-ruby-11 text-sm flex items-center justify-between"
    >
      <span>{{ errorMessage }}</span>
      <button class="ml-2 text-n-ruby-11 font-bold" @click="errorMessage = ''">
        x
      </button>
    </div>
    <div
      v-if="successMessage"
      class="p-3 rounded-md bg-n-teal-2 text-n-teal-11 text-sm flex items-center justify-between"
    >
      <span>{{ successMessage }}</span>
      <button
        class="ml-2 text-n-teal-11 font-bold"
        @click="successMessage = ''"
      >
        x
      </button>
    </div>

    <!-- Add store form -->
    <div
      v-if="showAddForm"
      class="outline outline-n-container outline-1 bg-n-alpha-3 rounded-md shadow p-5"
    >
      <h4 class="text-base font-medium text-n-slate-12 mb-4">
        Conectar nueva tienda PrestaShop
      </h4>
      <div class="flex flex-col gap-3">
        <div>
          <label class="block text-sm font-medium text-n-slate-11 mb-1">
            Nombre de la tienda *
          </label>
          <input
            v-model="formStoreName"
            type="text"
            placeholder="Mi Tienda"
            class="w-full px-3 py-2 rounded-md border border-n-weak bg-n-alpha-1 text-n-slate-12 text-sm focus:outline-none focus:ring-2 focus:ring-n-blue-7"
          />
        </div>
        <div>
          <label class="block text-sm font-medium text-n-slate-11 mb-1">
            URL PrestaShop *
          </label>
          <input
            v-model="formUrl"
            type="text"
            placeholder="https://tutienda.com"
            class="w-full px-3 py-2 rounded-md border border-n-weak bg-n-alpha-1 text-n-slate-12 text-sm focus:outline-none focus:ring-2 focus:ring-n-blue-7"
          />
        </div>
        <div>
          <label class="block text-sm font-medium text-n-slate-11 mb-1">
            API Key WebService *
          </label>
          <input
            v-model="formApiKey"
            type="password"
            placeholder="Clave generada en PrestaShop > Webservice"
            class="w-full px-3 py-2 rounded-md border border-n-weak bg-n-alpha-1 text-n-slate-12 text-sm focus:outline-none focus:ring-2 focus:ring-n-blue-7"
          />
        </div>
        <div>
          <label class="block text-sm font-medium text-n-slate-11 mb-1">
            Carpeta admin (para enlace al BO)
          </label>
          <input
            v-model="formAdminPath"
            type="text"
            placeholder="admin1234abc"
            class="w-full px-3 py-2 rounded-md border border-n-weak bg-n-alpha-1 text-n-slate-12 text-sm focus:outline-none focus:ring-2 focus:ring-n-blue-7"
          />
          <p class="text-xs text-n-slate-10 mt-1">
            Nombre de la carpeta de administracion de PrestaShop (ej: admin1234). Se encuentra en la URL del Back Office.
          </p>
        </div>
        <!-- Inbox mapping -->
        <div>
          <label class="block text-sm font-medium text-n-slate-11 mb-1">
            Asignar a inboxes (opcional)
          </label>
          <p class="text-xs text-n-slate-10 mb-2">
            Si seleccionas inboxes, los contactos que escriban por estos canales se asociaran automaticamente a esta tienda.
          </p>
          <div class="flex flex-wrap gap-2">
            <label
              v-for="inbox in inboxes"
              :key="inbox.id"
              class="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md border cursor-pointer text-sm transition-colors"
              :class="formInboxIds.includes(String(inbox.id))
                ? 'border-n-blue-7 bg-n-blue-2 text-n-blue-11'
                : 'border-n-weak bg-n-alpha-1 text-n-slate-11 hover:bg-n-alpha-2'"
            >
              <input
                type="checkbox"
                :checked="formInboxIds.includes(String(inbox.id))"
                class="hidden"
                @change="toggleInboxId(formInboxIds, inbox.id)"
              />
              {{ inbox.name }}
            </label>
          </div>
        </div>

        <!-- Test connection result -->
        <div
          v-if="testResult"
          class="p-2 rounded-md text-sm"
          :class="testResult.success
            ? 'bg-n-teal-2 text-n-teal-11'
            : 'bg-n-ruby-2 text-n-ruby-11'"
        >
          {{ testResult.message }}
        </div>

        <div class="flex gap-2 mt-2">
          <Button
            blue
            :label="savingStore ? 'Guardando...' : 'Conectar'"
            :disabled="savingStore"
            @click="addStore"
          />
          <Button
            faded
            slate
            :label="testingConnection ? 'Probando...' : 'Test conexion'"
            :disabled="testingConnection || !formUrl || !formApiKey"
            @click="handleTestConnection"
          />
          <Button
            faded
            slate
            label="Cancelar"
            @click="showAddForm = false"
          />
        </div>
      </div>
    </div>

    <!-- Connected stores -->
    <div
      v-if="stores.length > 0"
      class="outline outline-n-container outline-1 bg-n-alpha-3 rounded-md shadow p-5"
    >
      <h4 class="text-base font-medium text-n-slate-12 mb-4">
        Tiendas conectadas ({{ stores.length }})
      </h4>
      <div class="flex flex-col gap-3">
        <div
          v-for="storeItem in stores"
          :key="storeItem.id"
          class="p-4 rounded-md border border-n-weak bg-n-alpha-1"
        >
          <!-- View mode -->
          <div v-if="editingHookId !== storeItem.id">
            <div class="flex items-center justify-between mb-2">
              <div class="flex items-center gap-2">
                <h5 class="text-sm font-medium text-n-slate-12">
                  {{ storeItem.store_name }}
                </h5>
                <span
                  class="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium"
                  :class="storeItem.enabled
                    ? 'bg-n-teal-3 text-n-teal-11'
                    : 'bg-n-ruby-3 text-n-ruby-11'"
                >
                  {{ storeItem.enabled ? 'Activa' : 'Desactivada' }}
                </span>
              </div>
              <div class="flex gap-2">
                <Button
                  faded
                  blue
                  xs
                  label="Editar"
                  @click="startEdit(storeItem)"
                />
                <Button
                  faded
                  ruby
                  xs
                  label="Eliminar"
                  @click="openDeleteDialog(storeItem)"
                />
              </div>
            </div>
            <p class="text-xs text-n-slate-10">{{ storeItem.url }}</p>
            <div
              v-if="storeItem.inbox_ids && storeItem.inbox_ids.length > 0"
              class="mt-2 flex flex-wrap gap-1"
            >
              <span class="text-xs text-n-slate-10 mr-1">Inboxes:</span>
              <span
                v-for="inboxId in storeItem.inbox_ids"
                :key="inboxId"
                class="inline-flex items-center px-2 py-0.5 rounded-full text-xs bg-n-alpha-5 text-n-slate-11"
              >
                {{ getInboxName(inboxId) }}
              </span>
            </div>
          </div>

          <!-- Edit mode -->
          <div v-else class="flex flex-col gap-3">
            <div>
              <label class="block text-xs font-medium text-n-slate-11 mb-1">
                Nombre
              </label>
              <input
                v-model="editStoreName"
                type="text"
                class="w-full px-3 py-2 rounded-md border border-n-weak bg-n-alpha-1 text-n-slate-12 text-sm focus:outline-none focus:ring-2 focus:ring-n-blue-7"
              />
            </div>
            <div>
              <label class="block text-xs font-medium text-n-slate-11 mb-1">
                URL
              </label>
              <input
                v-model="editUrl"
                type="text"
                class="w-full px-3 py-2 rounded-md border border-n-weak bg-n-alpha-1 text-n-slate-12 text-sm focus:outline-none focus:ring-2 focus:ring-n-blue-7"
              />
            </div>
            <div>
              <label class="block text-xs font-medium text-n-slate-11 mb-1">
                Nueva API Key (dejar vacio para mantener la actual)
              </label>
              <input
                v-model="editApiKey"
                type="password"
                placeholder="Dejar vacio para no cambiar"
                class="w-full px-3 py-2 rounded-md border border-n-weak bg-n-alpha-1 text-n-slate-12 text-sm focus:outline-none focus:ring-2 focus:ring-n-blue-7"
              />
            </div>
            <div>
              <label class="block text-xs font-medium text-n-slate-11 mb-1">
                Carpeta admin (para enlace al BO)
              </label>
              <input
                v-model="editAdminPath"
                type="text"
                placeholder="admin1234abc"
                class="w-full px-3 py-2 rounded-md border border-n-weak bg-n-alpha-1 text-n-slate-12 text-sm focus:outline-none focus:ring-2 focus:ring-n-blue-7"
              />
            </div>
            <div>
              <label class="block text-xs font-medium text-n-slate-11 mb-1">
                Inboxes asociados
              </label>
              <div class="flex flex-wrap gap-2">
                <label
                  v-for="inbox in inboxes"
                  :key="inbox.id"
                  class="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md border cursor-pointer text-sm transition-colors"
                  :class="editInboxIds.includes(String(inbox.id))
                    ? 'border-n-blue-7 bg-n-blue-2 text-n-blue-11'
                    : 'border-n-weak bg-n-alpha-1 text-n-slate-11 hover:bg-n-alpha-2'"
                >
                  <input
                    type="checkbox"
                    :checked="editInboxIds.includes(String(inbox.id))"
                    class="hidden"
                    @change="toggleInboxId(editInboxIds, inbox.id)"
                  />
                  {{ inbox.name }}
                </label>
              </div>
            </div>

            <!-- Edit test connection -->
            <div
              v-if="editTestResult"
              class="p-2 rounded-md text-sm"
              :class="editTestResult.success
                ? 'bg-n-teal-2 text-n-teal-11'
                : 'bg-n-ruby-2 text-n-ruby-11'"
            >
              {{ editTestResult.message }}
            </div>

            <div class="flex gap-2 mt-1">
              <Button
                blue
                xs
                :label="savingEdit ? 'Guardando...' : 'Guardar'"
                :disabled="savingEdit"
                @click="saveEdit"
              />
              <Button
                faded
                slate
                xs
                :label="editTestingConnection ? 'Probando...' : 'Test'"
                :disabled="editTestingConnection || !editUrl"
                @click="handleEditTestConnection"
              />
              <Button faded slate xs label="Cancelar" @click="cancelEdit" />
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Empty state -->
    <div
      v-else-if="!loadingStores && !showAddForm"
      class="outline outline-n-container outline-1 bg-n-alpha-3 rounded-md shadow p-6 text-center"
    >
      <p class="text-n-slate-11 text-sm mb-3">
        No hay tiendas conectadas. Añade tu primera tienda PrestaShop.
      </p>
      <Button
        blue
        label="Añadir tienda"
        @click="showAddForm = true"
      />
    </div>

    <!-- Search orders -->
    <div
      v-if="stores.length > 0"
      class="outline outline-n-container outline-1 bg-n-alpha-3 rounded-md shadow p-5"
    >
      <h4 class="text-base font-medium text-n-slate-12 mb-4">
        Buscar pedidos
      </h4>
      <div class="flex flex-col gap-3">
        <div class="flex gap-3 flex-col sm:flex-row">
          <select
            v-model="searchHookId"
            class="px-3 py-2 rounded-md border border-n-weak bg-n-alpha-1 text-n-slate-12 text-sm focus:outline-none focus:ring-2 focus:ring-n-blue-7"
          >
            <option :value="null" disabled>
              Seleccionar tienda
            </option>
            <option
              v-for="storeItem in stores"
              :key="storeItem.id"
              :value="storeItem.id"
            >
              {{ storeItem.store_name }}
            </option>
          </select>
          <select
            v-model="searchType"
            class="px-3 py-2 rounded-md border border-n-weak bg-n-alpha-1 text-n-slate-12 text-sm focus:outline-none focus:ring-2 focus:ring-n-blue-7"
          >
            <option value="email">Email</option>
            <option value="phone">Telefono</option>
            <option value="reference">Referencia</option>
            <option value="order_id">ID Pedido</option>
          </select>
          <input
            v-model="searchQuery"
            type="text"
            :placeholder="searchType === 'email' ? 'cliente@email.com' :
              searchType === 'phone' ? '+34612345678' :
              searchType === 'reference' ? 'ABCDEFGH' : '12345'"
            class="flex-1 px-3 py-2 rounded-md border border-n-weak bg-n-alpha-1 text-n-slate-12 text-sm focus:outline-none focus:ring-2 focus:ring-n-blue-7"
            @keyup.enter="searchOrders"
          />
          <Button
            blue
            :label="searching ? 'Buscando...' : 'Buscar'"
            :disabled="searching"
            @click="searchOrders"
          />
        </div>

        <!-- Customer info -->
        <div
          v-if="searchCustomer"
          class="p-3 rounded-md bg-n-alpha-2 text-sm"
        >
          <span class="font-medium text-n-slate-12">Cliente:</span>
          <span class="text-n-slate-11 ml-1">
            {{ searchCustomer.firstname }} {{ searchCustomer.lastname }}
          </span>
          <span v-if="searchCustomer.email" class="text-n-slate-10 ml-2">
            ({{ searchCustomer.email }})
          </span>
        </div>

        <!-- Search results -->
        <div v-if="searching" class="flex justify-center py-4">
          <Spinner size="small" color-scheme="primary" />
        </div>
        <div v-else-if="searchResults && searchResults.length === 0" class="text-center py-4">
          <p class="text-sm text-n-slate-10">No se encontraron pedidos</p>
        </div>
        <div v-else-if="searchResults && searchResults.length > 0">
          <div class="overflow-x-auto">
            <table class="w-full text-sm">
              <thead>
                <tr class="border-b border-n-container">
                  <th class="text-left py-2 px-3 text-n-slate-11 font-medium">
                    Ref
                  </th>
                  <th class="text-left py-2 px-3 text-n-slate-11 font-medium">
                    Fecha
                  </th>
                  <th class="text-left py-2 px-3 text-n-slate-11 font-medium">
                    Estado
                  </th>
                  <th class="text-right py-2 px-3 text-n-slate-11 font-medium">
                    Total
                  </th>
                  <th class="text-right py-2 px-3 text-n-slate-11 font-medium">
                    Accion
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="order in searchResults"
                  :key="order.id"
                  class="border-b border-n-weak hover:bg-n-alpha-2"
                >
                  <td class="py-2 px-3 font-medium text-n-slate-12">
                    {{ order.reference || `#${order.id}` }}
                  </td>
                  <td class="py-2 px-3 text-n-slate-11">
                    {{ formatDate(order.date_add) }}
                  </td>
                  <td class="py-2 px-3">
                    <span class="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-n-alpha-5 text-n-slate-11">
                      {{ order.state_name || `Estado ${order.current_state}` }}
                    </span>
                  </td>
                  <td class="py-2 px-3 text-right text-n-slate-12">
                    {{ formatCurrency(order.total_paid, order.currency) }}
                  </td>
                  <td class="py-2 px-3 text-right">
                    <Button
                      faded
                      blue
                      xs
                      label="Ver"
                      @click="viewOrderDetails(order)"
                    />
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <!-- Order details modal/panel -->
        <div
          v-if="selectedOrder"
          class="p-4 rounded-md border border-n-weak bg-n-alpha-1"
        >
          <div class="flex items-center justify-between mb-3">
            <h5 class="text-sm font-medium text-n-slate-12">
              Pedido {{ selectedOrder.reference || `#${selectedOrder.id}` }}
            </h5>
            <button
              class="text-n-slate-10 hover:text-n-slate-12"
              @click="closeOrderDetails"
            >
              x
            </button>
          </div>

          <div v-if="loadingOrderDetails" class="flex justify-center py-4">
            <Spinner size="small" color-scheme="primary" />
          </div>
          <div v-else-if="orderDetails" class="flex flex-col gap-3 text-sm">
            <div class="grid grid-cols-2 gap-2">
              <div>
                <span class="text-n-slate-10">Estado:</span>
                <span class="ml-1 text-n-slate-12 font-medium">
                  {{ orderDetails.state_name || `Estado ${orderDetails.current_state}` }}
                </span>
              </div>
              <div>
                <span class="text-n-slate-10">Fecha:</span>
                <span class="ml-1 text-n-slate-12">
                  {{ formatDate(orderDetails.date_add) }}
                </span>
              </div>
              <div>
                <span class="text-n-slate-10">Total:</span>
                <span class="ml-1 text-n-slate-12 font-medium">
                  {{ formatCurrency(orderDetails.total_paid, orderDetails.currency) }}
                </span>
              </div>
              <div>
                <span class="text-n-slate-10">Pago:</span>
                <span class="ml-1 text-n-slate-12">
                  {{ orderDetails.payment || '-' }}
                </span>
              </div>
            </div>

            <!-- Items -->
            <div v-if="orderDetails.items && orderDetails.items.length > 0">
              <h6 class="text-xs font-medium text-n-slate-11 mb-2 mt-2">
                Articulos ({{ orderDetails.items.length }})
              </h6>
              <div class="overflow-x-auto">
                <table class="w-full text-xs">
                  <thead>
                    <tr class="border-b border-n-container">
                      <th class="text-left py-1.5 px-2 text-n-slate-10">
                        Producto
                      </th>
                      <th class="text-center py-1.5 px-2 text-n-slate-10">
                        Cant.
                      </th>
                      <th class="text-right py-1.5 px-2 text-n-slate-10">
                        Precio
                      </th>
                      <th class="text-right py-1.5 px-2 text-n-slate-10">
                        Total
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr
                      v-for="item in orderDetails.items"
                      :key="item.id"
                      class="border-b border-n-weak"
                    >
                      <td class="py-1.5 px-2 text-n-slate-12">
                        {{ item.product_name }}
                        <span
                          v-if="item.product_reference"
                          class="text-n-slate-10 ml-1"
                        >
                          ({{ item.product_reference }})
                        </span>
                      </td>
                      <td class="py-1.5 px-2 text-center text-n-slate-11">
                        {{ item.product_quantity }}
                      </td>
                      <td class="py-1.5 px-2 text-right text-n-slate-11">
                        {{ formatCurrency(item.unit_price_tax_incl, orderDetails.currency) }}
                      </td>
                      <td class="py-1.5 px-2 text-right text-n-slate-12 font-medium">
                        {{ formatCurrency(item.total_price_tax_incl, orderDetails.currency) }}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            <!-- Shipping -->
            <div v-if="orderDetails.shipping_address" class="mt-2">
              <h6 class="text-xs font-medium text-n-slate-11 mb-1">
                Direccion de envio
              </h6>
              <p class="text-xs text-n-slate-11">
                {{ orderDetails.shipping_address.firstname }} {{ orderDetails.shipping_address.lastname }}<br />
                {{ orderDetails.shipping_address.address1 }}
                <template v-if="orderDetails.shipping_address.address2">
                  , {{ orderDetails.shipping_address.address2 }}
                </template>
                <br />
                {{ orderDetails.shipping_address.postcode }} {{ orderDetails.shipping_address.city }}
                <template v-if="orderDetails.shipping_address.country">
                  , {{ orderDetails.shipping_address.country }}
                </template>
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Delete dialog -->
    <Dialog
      ref="dialogRef"
      type="alert"
      title="Eliminar tienda"
      :description="`¿Estas seguro de que quieres eliminar la tienda '${hookToDelete?.store_name}'? Los contactos asociados mantendran sus datos.`"
      confirm-button-label="Eliminar"
      cancel-button-label="Cancelar"
      @confirm="confirmDelete"
    />
  </div>
  <div v-else class="flex items-center justify-center flex-1">
    <Spinner size="" color-scheme="primary" />
  </div>
</template>
